
import React from 'react';
import {
  Video,
  FileText,
  CreditCard,
  Layout,
  Settings,
  Ticket,
  Calendar,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Tv
} from 'lucide-react';

const ElearningAppDetail: React.FC = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-8 mb-20">
        <div className="max-w-4xl mx-auto text-center space-y-8 animate-in fade-in duration-700">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-bold uppercase tracking-wider">
            <Sparkles size={16} /> Dedicated & Branded
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 leading-tight">
            Akademi Digital dengan <br /> <span className="text-purple-600">Brand Anda Sendiri</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Platform elearning profesional yang terintegrasi. Kelola materi, pantau perkembangan student, dan terima pembayaran otomatis dalam satu sistem.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
             <button className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold shadow-xl shadow-purple-200 hover:bg-purple-700 hover:shadow-2xl transition-all">
                Mulai Bangun Platform
             </button>
             <button className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition-all">
                Lihat Demo LMS
             </button>
          </div>
        </div>

        {/* LMS Mockup */}
        <div className="mt-20 relative max-w-5xl mx-auto">
           <div className="bg-slate-900 rounded-[3rem] p-3 shadow-2xl overflow-hidden border-4 border-slate-800">
              <div className="bg-white rounded-[2.5rem] overflow-hidden min-h-[500px] flex">
                 <div className="w-16 md:w-64 bg-slate-50 border-r border-slate-100 p-4 hidden md:block">
                    <div className="h-8 w-8 bg-purple-600 rounded-lg mb-10 mx-auto"></div>
                    <div className="space-y-4">
                       {[1,2,3,4,5].map(i => <div key={i} className="h-4 w-full bg-slate-200 rounded-full"></div>)}
                    </div>
                 </div>
                 <div className="flex-1 p-8">
                    <div className="flex justify-between items-center mb-8">
                       <div className="h-6 w-32 bg-slate-100 rounded-full"></div>
                       <div className="flex gap-2">
                          <div className="h-8 w-8 bg-slate-100 rounded-full"></div>
                          <div className="h-8 w-8 bg-slate-100 rounded-full"></div>
                       </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       <div className="aspect-video bg-slate-100 rounded-2xl flex items-center justify-center relative group cursor-pointer overflow-hidden">
                          <img src="https://picsum.photos/seed/learn/800/450" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg z-10">
                             <Video className="text-purple-600" size={20} />
                          </div>
                       </div>
                       <div className="space-y-4">
                          <div className="h-4 w-3/4 bg-slate-100 rounded-full"></div>
                          <div className="h-32 w-full bg-slate-50 rounded-2xl"></div>
                          <div className="flex justify-between">
                             <div className="h-10 w-24 bg-purple-100 rounded-xl"></div>
                             <div className="h-10 w-24 bg-purple-600 rounded-xl"></div>
                          </div>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
           {/* Floating Xendit Badge */}
           <div className="absolute -bottom-6 -right-6 md:-right-12 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-xl text-blue-600"><CreditCard size={20} /></div>
              <div>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Terintegrasi</p>
                 <p className="text-sm font-bold text-slate-900">Xendit Payment</p>
              </div>
           </div>
        </div>
      </section>

      {/* Experience Sections */}
      <section className="py-24 bg-slate-50">
         <div className="container mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 gap-24 items-center">
               <div className="space-y-8">
                  <h2 className="text-purple-600 font-bold uppercase tracking-wider text-sm">Student Experience</h2>
                  <h3 className="text-4xl font-bold text-slate-900 leading-snug">Pembelajaran Tanpa Batas untuk Student</h3>
                  <div className="space-y-6">
                     <ExperienceItem 
                        icon={<Tv className="text-purple-500" />}
                        title="Konten Multi-Format"
                        description="Akses ribuan video pembelajaran berkualitas tinggi dan artikel pendalaman materi di mana saja."
                     />
                     <ExperienceItem 
                        icon={<Layout className="text-indigo-500" />}
                        title="Program Belajar & Event"
                        description="Gabungkan kursus ke dalam Program Belajar (Path) atau ikuti Event webinar eksklusif."
                     />
                     <ExperienceItem 
                        icon={<CreditCard className="text-blue-500" />}
                        title="Checkout Xendit"
                        description="Pembayaran instan via Transfer Bank, E-Wallet, atau Retail Store dengan verifikasi otomatis."
                     />
                  </div>
               </div>
               <div className="space-y-8">
                  <h2 className="text-blue-600 font-bold uppercase tracking-wider text-sm">Admin Mastery</h2>
                  <h3 className="text-4xl font-bold text-slate-900 leading-snug">Kontrol Penuh di Tangan Anda</h3>
                  <div className="bg-white p-8 rounded-[3rem] shadow-xl border border-slate-100 space-y-6">
                     <AdminFeature 
                        title="Content Management" 
                        desc="Kelola kursus, modul, video, dan artikel dengan editor yang intuitif." 
                     />
                     <AdminFeature 
                        title="Order Monitoring" 
                        desc="Pantau seluruh transaksi dan riwayat pesanan student secara real-time." 
                     />
                     <AdminFeature 
                        title="Subscription Engine" 
                        desc="Tawarkan akses 'All Course' melalui paket berlangganan bulanan atau tahunan." 
                     />
                     <AdminFeature 
                        title="Voucher & Promo" 
                        desc="Buat dan terbitkan voucher promosi untuk meningkatkan konversi penjualan." 
                     />
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* Key Modules Grid */}
      <section className="py-24 bg-white">
         <div className="container mx-auto px-4 md:px-8">
            <div className="text-center mb-16 space-y-4">
               <h2 className="text-3xl font-bold">Fitur Inti Platform</h2>
               <p className="text-slate-500">Ekosistem lengkap untuk pertumbuhan akademi digital Anda.</p>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
               <ModuleCard icon={<FileText />} title="Artikel & Quiz" color="bg-orange-50 text-orange-600" />
               <ModuleCard icon={<Video />} title="Video Hosting" color="bg-red-50 text-red-600" />
               <ModuleCard icon={<Ticket />} title="Sistem Voucher" color="bg-blue-50 text-blue-600" />
               <ModuleCard icon={<Calendar />} title="Booking Event" color="bg-green-50 text-green-600" />
            </div>
         </div>
      </section>

      {/* Monetization Banner */}
      <section className="py-20">
         <div className="container mx-auto px-4 md:px-8">
            <div className="bg-slate-900 rounded-[4rem] p-12 md:p-20 text-white relative overflow-hidden">
               <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                     <circle cx="0" cy="0" r="40" fill="purple" />
                     <circle cx="100" cy="100" r="60" fill="blue" />
                  </svg>
               </div>
               <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
                  <div className="space-y-6">
                     <h3 className="text-3xl md:text-5xl font-bold">Model Bisnis Fleksibel</h3>
                     <p className="text-slate-400 text-lg">Platform kami mendukung berbagai cara bagi Anda untuk memonetisasi keahlian Anda.</p>
                     <div className="space-y-4">
                        <div className="flex items-center gap-3">
                           <CheckCircle2 className="text-purple-400" />
                           <span>Penjualan Kursus Satuan</span>
                        </div>
                        <div className="flex items-center gap-3">
                           <CheckCircle2 className="text-purple-400" />
                           <span>Paket Berlangganan (Subscription)</span>
                        </div>
                        <div className="flex items-center gap-3">
                           <CheckCircle2 className="text-purple-400" />
                           <span>Tiket Event Berbayar</span>
                        </div>
                     </div>
                  </div>
                  <div className="bg-white/5 p-8 rounded-[2rem] border border-white/10 text-center">
                     <p className="text-sm font-medium text-slate-400 mb-4 uppercase">Siap untuk Mulai?</p>
                     <p className="text-2xl font-bold mb-8">Konsultasikan kebutuhan brand elearning Anda hari ini.</p>
                     <button className="w-full py-4 bg-purple-600 rounded-xl font-bold hover:bg-purple-700 transition-all flex items-center justify-center gap-2">
                        Hubungi Tim Kami <ArrowRight size={20} />
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </section>
    </div>
  );
};

const ExperienceItem: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
   <div className="flex gap-4 p-4 rounded-2xl hover:bg-white transition-all hover:shadow-md border border-transparent hover:border-slate-100">
      <div className="shrink-0 mt-1">{icon}</div>
      <div>
         <h4 className="font-bold text-slate-900">{title}</h4>
         <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
      </div>
   </div>
);

const AdminFeature: React.FC<{ title: string, desc: string }> = ({ title, desc }) => (
   <div className="flex gap-4">
      <div className="shrink-0"><Settings size={20} className="text-slate-400" /></div>
      <div>
         <h4 className="font-bold text-sm text-slate-800">{title}</h4>
         <p className="text-xs text-slate-500">{desc}</p>
      </div>
   </div>
);

const ModuleCard: React.FC<{ icon: React.ReactNode, title: string, color: string }> = ({ icon, title, color }) => (
   <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all text-center space-y-4">
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center mx-auto`}>
         {icon}
      </div>
      <span className="font-bold text-sm block">{title}</span>
   </div>
);

export default ElearningAppDetail;
